// src/components/products/ProductsList.jsx
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { showToast } from '../../utils/helpers';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import NegotiationModal from './NegotiationModal';

// Constants
const VIEW_MODES = {
  GRID: 'grid',
  LIST: 'list',
  DETAILS: 'details'
};

const SHARE_OPTIONS = {
  WHATSAPP: 'whatsapp',
  EMAIL: 'email',
  COPY: 'copy'
};

const LISTING_TYPES = {
  PRODUCT: 'product',
  SERVICE: 'service'
};

const ProductsList = ({ 
  products = [], 
  onProductUpdate, 
  setCurrentLimit, 
  setCurrentPage,
  pagination = {},
  isLoading = false,
  onLoadMore 
}) => {
  const [imageErrors, setImageErrors] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewMode, setViewMode] = useState(VIEW_MODES.LIST);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [isCopying, setIsCopying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [deleteConfirmProduct, setDeleteConfirmProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [negotiationProduct, setNegotiationProduct] = useState(null);
  const [negoMinPrice, setNegoMinPrice] = useState('');
  const [negoMaxPrice, setNegoMaxPrice] = useState('');
  const [isSavingNego, setIsSavingNego] = useState(false);
  const navigate = useNavigate();
  const observerRef = useRef();
  const loadMoreRef = useRef();

  // Memoized products array
  const safeProducts = useMemo(() =>
    Array.isArray(products) ? products : [],
    [products]
  );

  const isEmpty = useMemo(() =>
    safeProducts.length === 0 && !isLoading,
    [safeProducts, isLoading]
  );

  // Pagination data
  const {
    current_page = 1,
    total_pages = 1,
    total_products = 0,
    products_per_page = 10,
    has_previous = false,
    has_next = false
  } = pagination;

  // Infinite scroll setup
  useEffect(() => {
    if (!has_next || isLoading || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [has_next, isLoading, isLoadingMore]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !has_next) return;
    
    setIsLoadingMore(true);
    try {
      await onLoadMore?.(current_page + 1);
    } catch (error) {
      console.error('Error loading more products:', error);
      showToast('Failed to load more products', 'error');
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, has_next, current_page, onLoadMore]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage?.(newPage);
  }, [setCurrentPage]);

  const handleAddToinvoice = useCallback(async (id, slug) =>{
// showToast("Feature is coming soon")
navigate(`/new/invoice?xid=${id}&pname=${slug}`)
})

  // Handle limit change
  const handleLimitChange = useCallback((newLimit) => {
    setCurrentLimit?.(newLimit);
    setCurrentPage?.(1); // Reset to first page when changing limit
  }, [setCurrentLimit, setCurrentPage]);

  // Skeleton Loader Components
  const SkeletonProductCard = () => (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden animate-pulse">
      <div className="aspect-square bg-gray-200 relative overflow-hidden"></div>
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        <div className="flex justify-between items-center">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        </div>
        <div className="flex space-x-2">
          <div className="h-8 bg-gray-200 rounded flex-1"></div>
          <div className="h-8 bg-gray-200 rounded w-16"></div>
        </div>
      </div>
    </div>
  );

  const SkeletonProductRow = () => (
      <tr className="animate-pulse">
        <td className="px-4 py-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
            <div className="space-y-2 flex-1">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </td>
        <td className="px-4 py-4 hidden md:table-cell">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        </td>
        <td className="px-4 py-4 hidden lg:table-cell">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </td>
        <td className="px-4 py-4">
          <div className="flex space-x-2 justify-end">
            <div className="h-8 bg-gray-200 rounded w-16"></div>
            <div className="h-8 bg-gray-200 rounded w-16"></div>
          </div>
        </td>
      </tr>
  );

  // Pagination Controls Component
  const PaginationControls = () => {
    if (total_pages <= 1) return null;

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0 px-4 py-4 border-t border-gray-200 bg-white ">
        {/* Items per page selector */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">Show:</span>
          <select
            value={products_per_page}
            onChange={(e) => handleLimitChange(parseInt(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-sm text-gray-700">per page</span>
        </div>

        {/* Page info */}
        <div className="text-sm text-gray-700">
          Showing {(current_page - 1) * products_per_page + 1} to{' '}
          {Math.min(current_page * products_per_page, total_products)} of{' '}
          {total_products.toLocaleString()} products
        </div>

        {/* Page navigation */}
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handlePageChange(current_page - 1)}
            disabled={!has_previous || isLoading}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white  transition-colors"
          >
            Previous
          </button>
          
          {/* Page numbers */}
          {Array.from({ length: Math.min(5, total_pages) }, (_, i) => {
            let pageNum;
            if (total_pages <= 5) {
              pageNum = i + 1;
            } else if (current_page <= 3) {
              pageNum = i + 1;
            } else if (current_page >= total_pages - 2) {
              pageNum = total_pages - 4 + i;
            } else {
              pageNum = current_page - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                disabled={isLoading}
                className={`px-3 py-1 border text-sm font-medium transition-colors ${
                  current_page === pageNum
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-300 text-gray-700 hover:bg-white '
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => handlePageChange(current_page + 1)}
            disabled={!has_next || isLoading}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white  transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Load More Component for Infinite Scroll
  const LoadMoreTrigger = () => {
    if (!has_next) return null;

    return (
      <div ref={loadMoreRef} className="py-6 text-center">
        {isLoadingMore ? (
          <div className="flex items-center justify-center space-x-2 text-gray-600">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span>Loading more products...</span>
          </div>
        ) : (
          <button
            onClick={handleLoadMore}
            className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
          >
            Load More
          </button>
        )}
      </div>
    );
  };

  // Rest of your existing helper functions remain the same...
  const getProductImages = useCallback((product) => {
    if (!product) return [];
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images.map(img => img.image_url);
    }
    if (product.product_image) {
      const images = product.product_image.split(',');
      return images.map(img => img.trim()).filter(img => img);
    }
    return [];
  }, []);

  const getPrimaryImage = useCallback((product) => {
    const images = getProductImages(product);
    const currentIndex = currentImageIndex[product.id] || 0;
    return images.length > 0 ? images[currentIndex] : null;
  }, [getProductImages, currentImageIndex]);

  const getDeliveryOptions = useCallback((product) => {
    if (!product.delivery_options && !product.is_digital) return null;
    try {
      if (product.is_digital) return "Download Available";
      if (typeof product.delivery_options === 'string') {
        return JSON.parse(product.delivery_options);
      }
      return product.delivery_options;
    } catch (error) {
      console.error('Error parsing delivery options:', error);
      return null;
    }
  }, []);

  const getListingTypeDisplay = useCallback((product) => {
    const uploadType = product.upload_type || LISTING_TYPES.PRODUCT;
    return uploadType === LISTING_TYPES.SERVICE ? 'Service' : 'Product';
  }, []);

  const hasNegotiablePricing = useCallback((product) => {
    if (product.is_negotiable &&
      product.starting_price > 0 &&
      product.ending_price > 0 &&
      product.starting_price < product.ending_price) {
      return true;
    }
    return false;
  }, []);

  const getPriceDisplay = useCallback((product) => {
    const basePrice = product?.per_unit_cost || 0;
    if (hasNegotiablePricing(product)) {
      return `₦${product.starting_price?.toLocaleString()} - ₦${product.ending_price?.toLocaleString()}`;
    }
    return `₦${basePrice.toLocaleString()}`;
  }, [hasNegotiablePricing]);

  const getDeliverySummary = useCallback((product) => {
    const deliveryOptions = getDeliveryOptions(product);
    if (!deliveryOptions && !product.is_digital) return 'No delivery options';
    if (product.is_digital === true || product.is_digital > 0) return "Download Available";
    
    const options = [];
    if (deliveryOptions.pickup?.available) options.push('Pickup');
    if (deliveryOptions.delivery?.available) options.push('Delivery');
    
    return options.length > 0 ? options.join(' • ') : 'No delivery options';
  }, [getDeliveryOptions]);

  // Your existing event handlers (handleImageError, nextImage, prevImage, handleDelete, etc.)
  const handleImageError = useCallback((productId) => {
    setImageErrors(prev => ({ ...prev, [productId]: true }));
  }, []);

  const nextImage = useCallback((productId, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => {
      const product = safeProducts.find(p => p.id === productId);
      const images = getProductImages(product);
      const currentIndex = prev[productId] || 0;
      const nextIndex = (currentIndex + 1) % images.length;
      return { ...prev, [productId]: nextIndex };
    });
  }, [safeProducts, getProductImages]);
  const prevImage = useCallback((productId, e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => {
      const product = safeProducts.find(p => p.id === productId);
      const images = getProductImages(product);
      const currentIndex = prev[productId] || 0;
      const prevIndex = (currentIndex - 1 + images.length) % images.length;
      return { ...prev, [productId]: prevIndex };
    });
  }, [safeProducts, getProductImages]);

   // Product actions
  const handleDelete = useCallback((productId, productName) => {
    setDeleteConfirmProduct({ id: productId, name: productName });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmProduct) return;
    const { id: productId, name: productName } = deleteConfirmProduct;

    setIsDeleting(productId);
    setDeleteConfirmProduct(null);

    try {
      const response = await api.delete(`${API_CONFIG.PRODUCTS.DELETE_PRODUCT}/${productId}`);
      if (response.data.success) {
        showToast('Product deleted successfully', 'success');
        onProductUpdate?.();
        if (selectedProduct?.id === productId) {
          setSelectedProduct(null);
          setViewMode(VIEW_MODES.LIST);
        }
      } else {
        throw new Error(response.data.error || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showToast(error.message || 'Failed to delete product', 'error');
    } finally {
      setIsDeleting(null);
    }
  }, [deleteConfirmProduct, selectedProduct, onProductUpdate]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmProduct(null);
  }, []);

  const handleSaveNegotiation = useCallback(async () => {
    if (!negotiationProduct) return;

    const minPrice = parseFloat(negoMinPrice);
    const maxPrice = parseFloat(negoMaxPrice);

    if (isNaN(minPrice) || isNaN(maxPrice)) {
      showToast('Please enter valid price values', 'error');
      return;
    }

    if (minPrice >= maxPrice) {
      showToast('Minimum price must be lower than maximum price', 'error');
      return;
    }

    setIsSavingNego(true);

    try {
      const formData = new FormData();
      formData.append('product_id', negotiationProduct.product_id);
      formData.append('accept_negotiations', 'true');
      formData.append('min_price', negoMinPrice);
      formData.append('max_price', negoMaxPrice);

      const response = await api.post(API_CONFIG.PRODUCTS.MAKE_NEGOTIABLE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        showToast('Negotiation range saved successfully', 'success');
        setNegotiationProduct(null);
        onProductUpdate?.();
      } else {
        throw new Error(response.data.message || 'Failed to save negotiation range');
      }
    } catch (error) {
      console.error('Error saving negotiation range:', error);
      showToast(error.response?.data?.message || error.message || 'Failed to save negotiation range', 'error');
    } finally {
      setIsSavingNego(false);
    }
  }, [negotiationProduct, negoMinPrice, negoMaxPrice, onProductUpdate]);

  const handleViewDetails = useCallback((product) => {
    setSelectedProduct(product);
    setViewMode(VIEW_MODES.DETAILS);
    // Reset image index when viewing details
    setCurrentImageIndex(prev => ({ ...prev, [product.id]: 0 }));
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedProduct(null);
    setViewMode(VIEW_MODES.LIST);
  }, []);

const handleEditProduct = useCallback((product) => {
  navigate(`/edit/${product.product_id}`);
}, [navigate]);

  const handleShareProduct = useCallback((product) => {
    const baseUrl = window.location.origin;
    const productLink = `${baseUrl}/product/${product.slug || product.product_id}`;
    setShareLink(productLink);
    setShowShareModal(true);
  }, []);

  // Share functionality
  const copyToClipboard = useCallback(async () => {
    try {
      setIsCopying(true);
      await navigator.clipboard.writeText(shareLink);
      showToast('Link copied to clipboard!', 'success');
    } catch (err) {
      showToast('Failed to copy link', 'error');
    } finally {
      setTimeout(() => setIsCopying(false), 2000);
    }
  }, [shareLink]);

  const shareViaWhatsApp = useCallback(() => {
    const message = `Check out this product: ${shareLink}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  }, [shareLink]);

  const shareViaEmail = useCallback(() => {
    const subject = `Check out this product: ${selectedProduct?.product_name || 'Amazing Product'}`;
    const body = `I thought you might be interested in this product:\n\n${shareLink}\n\n${selectedProduct?.description || ''}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  }, [shareLink, selectedProduct]);

  // View mode toggle component
  const ViewModeToggle = () => (
    <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
      <button
        onClick={() => setViewMode(VIEW_MODES.GRID)}
        className={`p-1 sm:p-2 rounded-md transition-all duration-200 ${viewMode === VIEW_MODES.GRID
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
          }`}
        aria-label="Grid view"
      >
        <i className="bi bi-grid-3x3-gap text-xs sm:text-sm"></i>
      </button>
      <button
        onClick={() => setViewMode(VIEW_MODES.LIST)}
        className={`p-1 sm:p-2 rounded-md transition-all duration-200 ${viewMode === VIEW_MODES.LIST
            ? 'bg-white text-blue-600 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
          }`}
        aria-label="List view"
      >
        <i className="bi bi-list-ul text-xs sm:text-sm"></i>
      </button>
    </div>
  );

  // Product card component for grid view - UPDATED with new data
  const ProductCard = ({ product }) => {
    const productImages = getProductImages(product);
    const primaryImage = getPrimaryImage(product);
    const hasImages = productImages.length > 0;
    const hasMultipleImages = productImages.length > 1;
    const isDeletingProduct = isDeleting === product.id;
    const isNegotiable = hasNegotiablePricing(product);
    const priceDisplay = getPriceDisplay(product);
    const listingType = getListingTypeDisplay(product);
    const deliverySummary = getDeliverySummary(product);

    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group w-full">
        {/* Product Image */}
        <div
          className="aspect-square bg-gray-100 relative overflow-hidden cursor-pointer"
          onClick={() => handleViewDetails(product)}
        >
          {hasImages && primaryImage && !imageErrors[product.id] ? (
            <>
              <img
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                src={primaryImage}
                alt={product.product_name || 'Product image'}
                onError={(e) => { e.target.onerror = null; handleImageError(product.id); }}
              />

              {/* Image Navigation Arrows */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={(e) => prevImage(product.id, e)}
                    className="absolute left-1 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-black bg-opacity-50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center text-xs"
                  >
                    ‹
                  </button>
                  <button
                    onClick={(e) => nextImage(product.id, e)}
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-black bg-opacity-50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center text-xs"
                  >
                    ›
                  </button>
                </>
              )}

              {/* Image Counter */}
              {hasMultipleImages && (
                <div className="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full">
                  {((currentImageIndex[product.id] || 0) + 1)}/{productImages.length}
                </div>
              )}

              {/* Listing Type Badge */}
              <div className="absolute top-1 left-1">
                {!product.is_digital ? (
    
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${listingType === 'Service'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                    }`}>
                    listingType
                  </span>
                ) : (
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${listingType === 'Service'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                    }`}>
                    Digital Product
                  </span>
                )
                }

              </div>

              {/* Negotiable Badge */}
              {isNegotiable && (
                <div className="absolute top-1 right-1">
                  <span className="inline-flex px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                    Negotiable
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full bg-blue-100 flex items-center justify-center">
              <span className="text-xl sm:text-2xl font-bold text-blue-800">
                {(product.product_name || 'P').charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          {/* Quick Actions Overlay */}
          <div className="absolute top-1 right-1 sm:top-2 sm:right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleShareProduct(product);
              }}
              className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-full shadow-sm flex items-center justify-center hover:bg-white  transition-colors"
              aria-label="Share product"
            >
              <i className="bi bi-share text-gray-600 text-xs"></i>
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-2 sm:p-3 lg:p-4">
          <h3
            className="font-medium text-gray-900 mb-1 sm:mb-2 line-clamp-2 leading-tight cursor-pointer min-h-[2.5rem] flex items-center text-xs sm:text-sm"
            onClick={() => handleViewDetails(product)}
          >
            {product.product_name || 'Unnamed Product'}
          </h3>

          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <span className="text-sm sm:text-base font-bold text-blue-600 whitespace-nowrap">
              {priceDisplay}
            </span>
            <span className={`text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full whitespace-nowrap ${(product.inventory - product.total_sold) > 0 || product.upload_type === "service" || product.is_digital
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
              }`}>
              {(product.inventory - product.total_sold) > 0 || product.upload_type === "service" || product.is_digital ? 'In Stock' : 'Out of Stock'}
            </span>
          </div>

          {/* NEW: Delivery Options Summary */}
          <div className="mb-2">
            <span className="text-xs text-gray-500">
              {deliverySummary}
            </span>
          </div>

          {/* NEW: Service-specific info */}
          {listingType === 'Service' && product.upfront_payment_percentage > 0 && (
            <div className="mb-2">
              <span className="text-xs text-purple-600 font-medium">
                {product.upfront_payment_percentage}% upfront payment
              </span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-1 sm:space-x-2">
            <button
              onClick={() => handleViewDetails(product)}
              className="flex-1 bg-blue-600 text-white py-1.5 px-2 sm:py-2 sm:px-3 rounded hover:bg-blue-700 transition-colors text-xs font-medium text-center whitespace-nowrap"
            >
              View Details
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(product.product_id, product.product_name);
              }}
              disabled={isDeletingProduct}
              className="px-2 py-1.5 sm:px-3 sm:py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors text-xs font-medium whitespace-nowrap"
            >
              {isDeletingProduct ? (
                <i className="bi bi-arrow-clockwise animate-spin"></i>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Product row component for list view - responsive: cards on mobile, table rows on desktop
  const ProductRow = ({ product }) => {
    const productImages = getProductImages(product);
    const primaryImage = getPrimaryImage(product);
    const hasImages = productImages.length > 0;
    const isDeletingProduct = isDeleting === product.id;
    const isNegotiable = hasNegotiablePricing(product);
    const priceDisplay = getPriceDisplay(product);
    const listingType = getListingTypeDisplay(product);
    const deliverySummary = getDeliverySummary(product);
    const inStock = (product.inventory - product.total_sold) > 0 || product.upload_type === "service" || product.is_digital;

    return (
        <tr className="hover:bg-white transition-colors duration-150 border-b border-gray-200 last:border-b-0">
          <td className="px-4 py-4">
            <div className="flex items-center space-x-3 cursor-pointer min-w-0" onClick={() => handleViewDetails(product)}>
              <div className="flex-shrink-0 w-12 h-12 relative">
                {hasImages && primaryImage && !imageErrors[product.id] ? (
                  <img
                    className="w-full h-full rounded-lg object-cover"
                    src={primaryImage}
                    alt={product.product_name || 'Product'}
                    onError={(e) => { e.target.onerror = null; handleImageError(product.id); }}
                  />
                ) : (
                  <div className="w-full h-full bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="font-bold text-blue-800 text-sm">
                      {(product.product_name || 'P').charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div className="absolute -top-1 -right-1">
                  <span className={`inline-flex px-1 py-0.5 text-xs font-medium rounded-full ${
                    listingType === 'Service' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {listingType.charAt(0)}
                  </span>
                </div>
              </div>
              <div className="min-w-0 flex-1 overflow-hidden">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {product.product_name || 'Unnamed Product'}
                </div>
                <div className="text-xs text-gray-500 flex items-center space-x-2 mt-0.5 flex-wrap">
                  <span className={inStock ? 'text-green-700' : 'text-red-600'}>
                    {inStock ? 'In Stock' : 'Out of Stock'}
                  </span>
                  {isNegotiable && (
                    <>
                      <span>•</span>
                      <span className="text-green-700 font-medium">Negotiable</span>
                    </>
                  )}
                  <span className="hidden lg:inline">•</span>
                  <span className="hidden lg:inline text-gray-500 truncate max-w-[120px]">
                    {deliverySummary}
                  </span>
                </div>
              </div>
            </div>
          </td>

          <td className="px-4 py-4 hidden md:table-cell">
            <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
              {priceDisplay}
            </div>
            {isNegotiable && (
              <div className="text-xs text-green-700 font-medium mt-0.5">Negotiable</div>
            )}
          </td>

          <td className="px-4 py-4 hidden lg:table-cell">
            <div className="space-y-1">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                listingType === 'Service' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {!product.is_digital ? listingType : 'Digital Product'}
              </span>
              <div className="text-xs text-gray-500">{deliverySummary}</div>
            </div>
          </td>

          <td className="px-4 py-4">
            <div className="flex items-center space-x-2 justify-end">
              <button
                onClick={() => handleViewDetails(product)}
                className="inline-flex items-center px-3 py-2 text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                View
              </button>
              <button
                onClick={() => handleDelete(product.product_id, product.product_name)}
                disabled={isDeletingProduct}
                className="inline-flex items-center px-3 py-2 text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {isDeletingProduct ? (
                  <i className="bi bi-arrow-clockwise animate-spin"></i>
                ) : 'Delete'}
              </button>
            </div>
          </td>
        </tr>
    );
  };

  // Mobile card component - renders outside table, no markup issues
  const ProductMobileCard = ({ product }) => {
    const productImages = getProductImages(product);
    const primaryImage = getPrimaryImage(product);
    const hasImages = productImages.length > 0;
    const isDeletingProduct = isDeleting === product.id;
    const isNegotiable = hasNegotiablePricing(product);
    const priceDisplay = getPriceDisplay(product);
    const listingType = getListingTypeDisplay(product);
    const inStock = (product.inventory - product.total_sold) > 0 || product.upload_type === "service" || product.is_digital;

    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-3 overflow-hidden">
        <div className="flex items-start gap-3 p-3">
          <div
            className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 shrink-0 cursor-pointer relative"
            onClick={() => handleViewDetails(product)}
          >
            {hasImages && primaryImage && !imageErrors[product.id] ? (
              <img
                className="w-full h-full object-cover"
                src={primaryImage}
                alt={product.product_name || 'Product'}
                onError={(e) => { e.target.onerror = null; handleImageError(product.id); }}
              />
            ) : (
              <div className="w-full h-full bg-blue-100 flex items-center justify-center">
                <span className="font-bold text-blue-800 text-sm">
                  {(product.product_name || 'P').charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="absolute -top-1 -right-1">
              <span className={`inline-flex px-1 py-0.5 text-[10px] font-medium rounded-full ${
                listingType === 'Service' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {listingType.charAt(0)}
              </span>
            </div>
          </div>

          <div className="flex-1 min-w-0 cursor-pointer overflow-hidden" onClick={() => handleViewDetails(product)}>
            <div className="text-sm font-medium text-gray-900 truncate leading-snug">
              {product.product_name || 'Unnamed Product'}
            </div>
            <div className="text-xs text-gray-500 mt-1 flex items-center flex-wrap gap-x-1.5">
              <span className="font-semibold text-blue-600">{priceDisplay}</span>
              <span className="text-gray-300">|</span>
              <span className={inStock ? 'text-green-700' : 'text-red-600'}>
                {inStock ? 'In Stock' : 'Out of Stock'}
              </span>
              {isNegotiable && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className="text-green-700 font-medium">Negotiable</span>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 shrink-0">
            <button
              onClick={() => handleViewDetails(product)}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 transition-colors"
            >
              View
            </button>
            <button
              onClick={() => handleDelete(product.product_id, product.product_name)}
              disabled={isDeletingProduct}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg text-red-600 bg-red-50 hover:bg-red-100 active:bg-red-200 disabled:opacity-50 transition-colors"
            >
              {isDeletingProduct ? (
                <i className="bi bi-arrow-clockwise animate-spin"></i>
              ) : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Delete Confirmation Modal
  const DeleteConfirmModal = ({ product }) => {
    if (!product) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={handleCancelDelete}>
        <div className="bg-white rounded-xl w-full max-w-sm mx-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
          <div className="p-6 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Product</h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete <span className="font-medium text-gray-700">"{product.name}"</span>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelDelete}
                className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting === product.id}
                className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {isDeleting === product.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleCloseNegotiation = useCallback(() => {
    setNegotiationProduct(null);
  }, []);

  const handleNegoMinPriceChange = useCallback((e) => {
    const val = e.target.value.replace(/[^\d.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) return;
    if (parts.length === 2 && parts[1].length > 2) return;
    setNegoMinPrice(val);
  }, []);

  const handleNegoMaxPriceChange = useCallback((e) => {
    const val = e.target.value.replace(/[^\d.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) return;
    if (parts.length === 2 && parts[1].length > 2) return;
    setNegoMaxPrice(val);
  }, []);

  // Share Modal Component (unchanged)
  const ShareModal = ({ show, onClose }) => {
    if (!show) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
        <div className="bg-white rounded-xl w-full max-w-sm mx-auto shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Share Product
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
              aria-label="Close share modal"
            >
              <i className="bi bi-x-lg text-lg"></i>
            </button>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-4">
            <div className="mb-3 sm:mb-4">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Product Link
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="flex-1 px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white "
                />
                <button
                  onClick={copyToClipboard}
                  disabled={isCopying}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors ${isCopying
                      ? 'bg-green-600 text-white'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  {isCopying ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Share Options */}
            <div className="space-y-2">
              <button
                onClick={shareViaWhatsApp}
                className="w-full flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm font-medium"
              >
                <i className="bi bi-whatsapp mr-2"></i>
                Share via WhatsApp
              </button>
              <button
                onClick={shareViaEmail}
                className="w-full flex items-center justify-center px-3 sm:px-4 py-2 sm:py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-xs sm:text-sm font-medium"
              >
                <i className="bi bi-envelope mr-2"></i>
                Share via Email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Product Details View - UPDATED with new data
  const ProductDetails = ({ product, onBack }) => {
    if (!product) {
      return (
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-full overflow-hidden">
          <div className="text-center py-6 sm:py-8">
            <i className="bi bi-exclamation-triangle text-3xl sm:text-4xl text-yellow-500 mb-3 sm:mb-4"></i>
            <p className="text-gray-500 text-base sm:text-lg mb-4">Product not found</p>
            <button
              onClick={onBack}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              Back to Products
            </button>
          </div>
        </div>
      );
    }

    const productImages = getProductImages(product);
    const currentIndex = currentImageIndex[product.id] || 0;
    const currentImage = productImages[currentIndex] || null;
    const hasImages = productImages.length > 0;
    const hasMultipleImages = productImages.length > 1;
    const listingType = getListingTypeDisplay(product);
    const isNegotiable = hasNegotiablePricing(product);
    const priceDisplay = getPriceDisplay(product);
    const deliveryOptions = getDeliveryOptions(product);
    const isService = listingType === 'Service';

    const nextImageDetail = () => {
      setCurrentImageIndex(prev => ({
        ...prev,
        [product.id]: (currentIndex + 1) % productImages.length
      }));
    };

    const prevImageDetail = () => {
      setCurrentImageIndex(prev => ({
        ...prev,
        [product.id]: (currentIndex - 1 + productImages.length) % productImages.length
      }));
    };

    return (
      <div className="bg-white rounded-lg shadow-lg overflow-hidden w-full max-w-full">
        {/* Header */}
        <div className="bg-white  px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <button
              onClick={onBack}
              className="flex items-center text-blue-600 hover:text-blue-800 transition-colors text-xs sm:text-sm font-medium"
            >
              <i className="bi bi-arrow-left mr-1 sm:mr-2"></i>
              Back to Products
            </button>
            <div className="flex space-x-1 sm:space-x-2">
              <button
                onClick={() => handleEditProduct(product)}
                className="px-2 sm:px-3 lg:px-4 py-1 sm:py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs sm:text-sm font-medium"
              >
                Edit
              </button>
              <button
                onClick={() => handleShareProduct(product)}
                className="px-2 sm:px-3 lg:px-4 py-1 sm:py-2 border border-gray-300 text-gray-700 rounded hover:bg-white  transition-colors text-xs sm:text-sm font-medium"
              >
                Share
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 sm:p-4 lg:p-6 w-full max-w-full overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 w-full">
            {/* Image Section */}
            <div className="relative w-full">
              {hasImages && currentImage && !imageErrors[product.id] ? (
                <div className="relative">
                  <img
                    className="w-full object-cover rounded-lg shadow-md"
                    src={currentImage}
                    alt={product.product_name || 'Product image'}
                    onError={(e) => { e.target.onerror = null; handleImageError(product.id); }}
                  />

                  {/* Image Navigation */}
                  {hasMultipleImages && (
                    <>
                      <button
                        onClick={prevImageDetail}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all flex items-center justify-center"
                      >
                        ‹
                      </button>
                      <button
                        onClick={nextImageDetail}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-all flex items-center justify-center"
                      >
                        ›
                      </button>

                      {/* Image Counter */}
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-sm px-3 py-1 rounded-full">
                        {currentIndex + 1} / {productImages.length}
                      </div>
                    </>
                  )}

                  {/* Listing Type Badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${isService
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                      }`}>
                        {!product.is_digital ? (
                      listingType

                        ): (`Digital Product`)}
                    </span>
                  </div>

                  {/* Negotiable Badge */}
                  {isNegotiable && (
                    <div className="absolute top-2 right-2">
                      <span className="inline-flex px-3 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full">
                        Price Negotiable
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-48 sm:h-64 lg:h-80 bg-blue-100 rounded-lg shadow-md flex items-center justify-center">
                  <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-800">
                    {(product.product_name || 'P').charAt(0).toUpperCase()}
                  </span>
                </div>
              )}

              {/* Image Thumbnails */}
              {hasMultipleImages && (
                <div className="flex space-x-2 mt-3 sm:mt-4 overflow-x-auto pb-2">
                  {productImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(prev => ({ ...prev, [product.id]: index }))}
                      className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded border-2 ${index === currentIndex ? 'border-blue-600' : 'border-gray-300'
                        } overflow-hidden`}
                    >
                      <img
                        src={image}
                        alt={`${product.product_name} thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="space-y-3 sm:space-y-4 lg:space-y-6 w-full">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
                  {product.product_name || 'Unnamed Product'}
                </h1>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-2xl sm:text-3xl font-bold text-blue-600">
                    {priceDisplay}
                  </div>
                  <button
                    onClick={() => { setNegoMinPrice(product.starting_price || ''); setNegoMaxPrice(product.ending_price || product.per_unit_cost || ''); setNegotiationProduct(product); }}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg text-white bg-black hover:bg-gray-800 active:bg-gray-700 transition-colors"
                  >
                    {isNegotiable ? 'Edit Range' : 'Make negotiable'}
                  </button>
                </div>
                {isNegotiable && (
                  <div className="text-sm text-gray-700 font-medium mt-1">
                    Price range: ₦{product.starting_price?.toLocaleString()} - ₦{product.ending_price?.toLocaleString()}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">
                  Description
                </h3>
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                  {product.description || 'No description available.'}
                </p>
              </div>

              {/* NEW: Service-specific information */}
              {isService && product.upfront_payment_percentage > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4">
                  <h4 className="text-sm sm:text-base font-semibold text-purple-900 mb-2">
                    Service Payment Terms
                  </h4>
                  <div className="text-sm text-purple-700">
                    <strong>{product.upfront_payment_percentage}%</strong> upfront payment required
                  </div>
                </div>
              )}

              {/* NEW: Delivery Options */}
              {deliveryOptions && !product.is_digital && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                  <h4 className="text-sm sm:text-base font-semibold text-blue-900 mb-2">
                    Delivery Options
                  </h4>
                  <div className="space-y-2 text-sm text-blue-700">
                    {deliveryOptions.pickup?.available && (
                      <div className="flex items-center">
                        <i className="bi bi-check-circle text-green-500 mr-2"></i>
                        <span>Customer Pickup Available</span>
                      </div>
                    )}
                    {deliveryOptions.delivery?.available && (
                      <div className="flex items-center">
                        <i className="bi bi-check-circle text-green-500 mr-2"></i>
                        <span>Home Delivery Available</span>
                        {deliveryOptions.delivery.fees && (
                          <span className="ml-2 text-xs">
                            (Lagos: ₦{deliveryOptions.delivery.fees.lagos?.toLocaleString()},
                            Outside: ₦{deliveryOptions.delivery.fees.outside_lagos?.toLocaleString()})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Product Info Grid */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full">
                <div>
                  <div className="text-xs sm:text-sm text-gray-500">Category</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-900">
                    {product.category || 'Uncategorized'}
                  </div>
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-500">Type</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-900">
                    {!product.is_digital ? (
                      listingType
                    ): (
                      `Digital Product`
                    )}
                    {/* {listingType} */}
                  </div>
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-500">Stock</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-900">
                    {product.inventory - product.total_sold || 0} / {product.inventory || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-500">Condition</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-900">
                    {product.product_condition || 'Not specified'}
                  </div>
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-500">Product ID</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-900">
                    {product.product_id || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-500">Images</div>
                  <div className="text-xs sm:text-sm font-medium text-gray-900">
                    {productImages.length} image{productImages.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2 sm:space-x-3 pt-3 sm:pt-4 w-full">
                <button className="flex-1 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs sm:text-sm font-medium" onClick={() => handleAddToinvoice(product.product_id, product.slug)}>
                  Add to Invoice
                </button>
                <button
                  onClick={() => handleDelete(product.product_id, product.product_name)}
                  disabled={isDeleting === product.id}
                  className="flex-1 px-3 sm:px-4 lg:px-6 py-2 sm:py-3 bg-[#ef4444] text-white rounded hover:bg-[#ef444]-800 disabled:opacity-50 transition-colors text-xs sm:text-sm font-medium"
                >
                  {isDeleting === product.id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render logic

    if (viewMode === VIEW_MODES.DETAILS) {
    return (
      <>
        <ProductDetails product={selectedProduct} onBack={handleBackToList} />
        <ShareModal show={showShareModal} onClose={() => setShowShareModal(false)} />
        <DeleteConfirmModal product={deleteConfirmProduct} />
        <NegotiationModal
          product={negotiationProduct}
          minPrice={negoMinPrice}
          maxPrice={negoMaxPrice}
          onMinPriceChange={handleNegoMinPriceChange}
          onMaxPriceChange={handleNegoMaxPriceChange}
          onSave={handleSaveNegotiation}
          onClose={handleCloseNegotiation}
          isSaving={isSavingNego}
        />
      </>
    );
  }

  return (
    <div className="w-full max-w-full overflow-hidden bg-white rounded-lg shadow-sm">
      {/* Header with View Toggle and Product Count */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Products & Services
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {isLoading ? (
                <span className="inline-flex items-center">
                  <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Loading products...
                </span>
              ) : (
                `Showing ${safeProducts.length} of ${total_products.toLocaleString()} products`
              )}
            </p>
          </div>
          {/* Your existing ViewModeToggle component */}
        </div>
      </div>

      {/* Content */}
      <div className="w-full max-w-full overflow-hidden">
        {isLoading && safeProducts.length === 0 ? (
          // Initial loading state
          <div className="p-4 sm:p-6">
            {viewMode === VIEW_MODES.GRID ? (
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {Array.from({ length: 8 }, (_, i) => (
                  <SkeletonProductCard key={i} />
                ))}
              </div>
            ) : (
              <>
                {/* Mobile skeleton cards */}
                <div className="md:hidden">
                  {Array.from({ length: 5 }, (_, i) => (
                    <div key={i} className="bg-white rounded-xl border border-gray-200 shadow-sm mb-3 overflow-hidden animate-pulse">
                      <div className="flex items-start gap-3 p-3">
                        <div className="w-14 h-14 bg-gray-200 rounded-lg shrink-0"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                          <div className="flex gap-2 pt-1">
                            <div className="w-14 h-7 bg-gray-200 rounded-lg"></div>
                            <div className="w-14 h-7 bg-gray-200 rounded-lg"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop skeleton table */}
                <div className="hidden md:block rounded-lg border border-gray-200 overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Listing</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Price</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Type & Delivery</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 5 }, (_, i) => (
                        <SkeletonProductRow key={i} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        ) : isEmpty ? (
          // Empty state
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="bi bi-inbox text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products or services yet</h3>
            <p className="text-gray-500 text-sm mb-6">Get started by creating your first listing.</p>
          </div>
        ) : (
          // Products list
          <div className="p-4 sm:p-6">
            {viewMode === VIEW_MODES.GRID ? (
              <>
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                  {safeProducts.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                  
                  {/* Additional skeleton cards when loading more */}
                  {isLoadingMore && Array.from({ length: 4 }, (_, i) => (
                    <SkeletonProductCard key={`skeleton-${i}`} />
                  ))}
                </div>
                <LoadMoreTrigger />
              </>
            ) : (
              <>
                {/* Mobile cards - plain divs, no table involvement */}
                <div className="md:hidden">
                  {safeProducts.map((product) => (
                    <ProductMobileCard key={product.id} product={product} />
                  ))}
                  {isLoadingMore && Array.from({ length: 3 }, (_, i) => (
                    <div key={`skel-mob-${i}`} className="bg-white rounded-xl border border-gray-200 shadow-sm mb-3 overflow-hidden animate-pulse">
                      <div className="flex items-start gap-3 p-3">
                        <div className="w-14 h-14 bg-gray-200 rounded-lg shrink-0"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                          <div className="flex gap-2 pt-1">
                            <div className="w-14 h-7 bg-gray-200 rounded-lg"></div>
                            <div className="w-14 h-7 bg-gray-200 rounded-lg"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <LoadMoreTrigger />
                </div>
                {/* Desktop table */}
                <div className="hidden md:block rounded-lg border border-gray-200 overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Listing</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Price</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Type & Delivery</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {safeProducts.map((product) => (
                        <ProductRow key={product.id} product={product} />
                      ))}
                      {isLoadingMore && Array.from({ length: 3 }, (_, i) => (
                        <SkeletonProductRow key={`skeleton-${i}`} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Pagination Controls */}
        <PaginationControls />
      </div>

      <ShareModal show={showShareModal} onClose={() => setShowShareModal(false)} />
      <DeleteConfirmModal product={deleteConfirmProduct} />
      <NegotiationModal
        product={negotiationProduct}
        minPrice={negoMinPrice}
        maxPrice={negoMaxPrice}
        onMinPriceChange={handleNegoMinPriceChange}
        onMaxPriceChange={handleNegoMaxPriceChange}
        onSave={handleSaveNegotiation}
        onClose={handleCloseNegotiation}
        isSaving={isSavingNego}
      />
    </div>
  );
};

// Make sure to include your existing ProductCard, ProductRow, ShareModal, and other components

export default ProductsList;